import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, getToken } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch {
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id / store the token and establish session first.
    if (typeof window !== "undefined" &&
        (window.location.hash?.includes("session_id=") || window.location.hash?.includes("ms_token="))) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const register = async (data) => {
    const r = await api.post("/auth/register", data);
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const loginWithGoogleSession = async (sessionId) => {
    const r = await api.post("/auth/google/session", { session_id: sessionId });
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const changePassword = async (payload) => {
    await api.post("/auth/change-password", payload);
    await refresh();
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setToken(null);
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, loginWithGoogleSession, logout, refresh, setUser, changePassword }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
